import { BillingPlanIds } from '../../../../../enums/billing-plan-ids';
import { planFeatureMessages } from './messages';
import { FallbackShipmentLimit, FallbackPrice } from './constants';

/** @typedef {import('./types.d.ts').PlanDetails} PlanDetails */
/** @typedef {import('./types.d.ts').Price} Price */
/** @typedef {import('./types.d.ts').ShipmentLimitTier} ShipmentLimitTier */
/** @typedef {import('./types.d.ts').RedactedCoupon} RedactedCoupon */

/**
 * Returns the first shipmentLimit endingQuantity that matches or exceeds the user's current shipment limit
 * @param {ShipmentLimitTier[]} shipmentLimits
 * @param {PlanDetails} currentPlanDetails
 * @returns {ShipmentLimitTier}
 */
export const getPromotionalShipmentLimit = (shipmentLimits, currentPlanDetails) => {
  const currentShipmentLimit = currentPlanDetails.shipmentLimit?.limit ?? FallbackShipmentLimit;

  const shipmentLimitsOrdered = shipmentLimits.sort((a, b) => a.endingQuantity - b.endingQuantity);
  const equivalentOrGreaterLimit = shipmentLimitsOrdered.find((sl) => sl.endingQuantity >= currentShipmentLimit);

  if (!equivalentOrGreaterLimit) {
    // we have no shipmentLimit to offer. Log something egregious!
  }

  return {
    limit: equivalentOrGreaterLimit?.endingQuantity ?? currentShipmentLimit,
    price: equivalentOrGreaterLimit?.price ?? FallbackPrice,
  };
};

/**
 *
 * @param {Price} planPricePerMonth
 * @param {RedactedCoupon} coupon
 * @returns {Price}
 */
export const getPromotionalPricePerMonth = (planPricePerMonth, coupon) => {
  const newValue = planPricePerMonth.value * (coupon.discountPercent / 100);
  return {
    ...planPricePerMonth,
    value: newValue,
  };
};

/**
 *  Returns the total monthly price of the plan. The value accounts for additionalUsers costs when applicable
 * @param {object} args
 * @param {Price} args.basePlanPrice
 * @param {number} args.shipmentLimitPrice
 * @param {number} args.additionalUserCount
 * @param {number} args.additionalUserPrice
 * @param {number} args.legacyLineItemAddOnsPrice
 * @returns {Price}
 */
export const getPlanTotalPrice = ({
  additionalUserCount = 0,
  additionalUserPrice = 0,
  basePlanPrice = FallbackPrice,
  legacyLineItemAddOnsPrice = 0,
  shipmentLimitPrice = 0,
}) => {
  const totalWithoutAdditionallUsers = (basePlanPrice?.value || 0) + shipmentLimitPrice + legacyLineItemAddOnsPrice;
  const totalAdditionalUserPrice = additionalUserCount * additionalUserPrice;
  const totalPriceValue = additionalUserCount <= 0 ? totalWithoutAdditionallUsers : totalWithoutAdditionallUsers + totalAdditionalUserPrice;

  return {
    ...basePlanPrice, // all currency values are assumed to be the same
    value: totalPriceValue,
  };
};

export const getDiscountedValues = ({ fullPrice, discountPercentage }) => {
  const percentage = discountPercentage > 1 ? discountPercentage / 100 : discountPercentage;
  const discountedPrice = fullPrice * (1 - percentage);
  return {
    discountedPrice,
    discountedSaving: fullPrice - discountedPrice,
  };
};

const billingPlanPromotionalFeatures = {
  [BillingPlanIds.Standard2025Q2]: planFeatureMessages.standardPlan,
  [BillingPlanIds.Premium2025Q2]: planFeatureMessages.premiumPlan,
};

export const getPromotionalFeatures = (billingPlanId) => {
  return billingPlanPromotionalFeatures[billingPlanId] ?? planFeatureMessages.defaultMessage;
};
